import { useState, FormEvent, useEffect, useRef } from "react";

export interface CreateEntityField {
  name: string;
  label: string;
  type?: "text" | "number" | "password";
  placeholder?: string;
}

export interface CreateEntityProps {
  title: string;
  fields: CreateEntityField[];
  onSubmit: (values: Record<string, string>) => Promise<{ successMessage?: string }>;
  onClose?: () => void;
  submitLabel?: string;
  initialValues?: Record<string, string>;
}

export default function CreateEntity({
  title,
  fields,
  onSubmit,
  onClose,
  submitLabel = "Create",
  initialValues = {}
}: CreateEntityProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of fields) {
      initial[field.name] = initialValues[field.name] ?? "";
    }
    return initial;
  });

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-focus first input when component mounts or fields change
  useEffect(() => {
    if (fields.length > 0 && firstInputRef.current) {
      // Small delay to ensure component is fully rendered
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [fields.length]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    setIsLoading(true);

    onSubmit(values)
      .then((result) => {
        if (result.successMessage) {
          setSuccess(result.successMessage);
          // Auto-dismiss success message after 5 seconds
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
          }
          successTimeoutRef.current = setTimeout(() => {
            setSuccess("");
          }, 5000);
        }
        // reset values to initial
        setValues(() => {
          const reset: Record<string, string> = {};
          for (const field of fields) {
            reset[field.name] = initialValues[field.name] ?? "";
          }
          return reset;
        });
        if (onClose) {
          setTimeout(() => onClose(), 1500);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Operation failed. Please try again.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem"
        }}
      >
        <h3 data-testid="create-entity-title">{title}</h3>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: "0.25rem 0.5rem",
              fontSize: "0.875rem",
              cursor: "pointer",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px"
            }}
          >
            Close
          </button>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {fields.map((field, index) => (
          <label key={field.name}>
            {field.label}
            <input
              ref={index === 0 ? firstInputRef : undefined}
              type={field.type ?? "text"}
              value={values[field.name] ?? ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              data-testid={`create-entity-input-${field.name}`}
              style={{
                width: "100%",
                padding: "0.5rem",
                marginTop: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ced4da",
                borderRadius: "4px"
              }}
              placeholder={field.placeholder}
            />
          </label>
        ))}

        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="create-entity-submit-button"
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? "Processing..." : submitLabel}
          </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-testid="create-entity-cancel-button"
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: "pointer",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px"
            }}
          >
            Cancel
          </button>
        )}
        </div>

        {error && (
          <div data-testid="create-entity-error-message" style={{ 
            color: "red", 
            marginTop: "0.5rem",
            padding: "0.75rem",
            backgroundColor: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem"
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError("")}
              style={{
                background: "none",
                border: "none",
                color: "red",
                cursor: "pointer",
                fontSize: "1.2rem",
                fontWeight: "bold",
                padding: "0",
                minWidth: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              title="Close"
            >
              ×
            </button>
          </div>
        )}
        {success && (
          <p data-testid="create-entity-success-message" style={{ 
            color: "green", 
            marginTop: "0.5rem",
            padding: "0.75rem",
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "4px"
          }}>
            {success}
          </p>
        )}
      </form>
    </div>
  );
}


